import api from "./apiService";

/**
 * Fetch all events accessible to the current user
 * @param {Object} options - Options for the fetch request
 * @param {number} options.communityId - Optional community ID to filter events
 * @returns {Promise<Array>} List of events
 */
export const fetchEvents = async (options = {}) => {
    try {
        let url = "/events";
        const params = new URLSearchParams();
        
        // Add community filter if provided
        if (options.communityId) {
            params.append('community_id', options.communityId);
        }
        
        // Add date filter if provided
        if (options.date) {
            params.append('date', options.date);
        }
        
        // Add the query parameters to the URL if there are any
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch an event by ID with options
 * @param {number} id - The event ID
 * @param {Object} options - Options for the fetch request
 * @param {boolean} options.includeAttendees - Whether to include attendees
 * @returns {Promise<Object>} Event data
 */
export const fetchEventById = async (id, options = {}) => {
    try {
        let url = `/events/${id}`;

        // Add query parameters if needed
        if (options.includeAttendees) {
            url += '?include_attendees=true';
        }

        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Fetch events for a specific date
 * @param {string} date - The date string in YYYY-MM-DD format
 * @param {number} communityId - Optional community ID to filter events
 * @returns {Promise<Array>} List of events for the date
 */
export const fetchEventsByDate = async (date, communityId = null) => {
    try {
        const formattedDate = new Date(date).toISOString().split("T")[0];

        let url = `/events?date=${formattedDate}`;
        if (communityId) {
            url += `&community_id=${communityId}`;
        }

        const response = await api.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Create a new event
 * @param {Object} eventData - The event data to create
 * @returns {Promise<Object>} Created event data
 */
export const createEvent = async (eventData) => {
    try {
        const response = await api.post("/events", eventData);

        if (!response.data || !response.data.event) {
            throw new Error("Invalid server response - no event data received");
        }
        return response.data.event;
    } catch (error) {
        throw error;
    }
};

/**
 * Update an existing event
 * @param {number} eventId - The ID of the event to update
 * @param {Object} eventData - The updated event data
 * @returns {Promise<Object>} Updated event data
 */
export const updateEvent = async (eventId, eventData) => {
    // Create a copy to avoid modifying the original
    const dataToSend = { ...eventData };

    // Ensure proper types for important fields
    if (dataToSend.community_id !== null && dataToSend.community_id !== undefined) {
        // Ensure it's a number
        dataToSend.community_id = parseInt(dataToSend.community_id);
    }

    try {
        const response = await api.put(`/events/${eventId}`, dataToSend);

        if (!response.data || !response.data.event) {
            throw new Error("Invalid server response - no event data received");
        }

        return response.data.event;
    } catch (error) {
        throw error;
    }
};

/**
 * Delete an event
 * @param {number} eventId - The ID of the event to delete
 * @returns {Promise<Object>} Response data
 */
export const deleteEvent = async (eventId) => {
    try {
        const response = await api.delete(`/events/${eventId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Add attendees to an event
 * @param {number} eventId - The ID of the event
 * @param {Array} userIds - Array of user IDs to add as attendees
 * @returns {Promise<Object>} Response data
 */
export const addEventAttendees = async (eventId, userIds) => {
    try {
        const response = await api.post(`/events/${eventId}/attendees`, { user_ids: userIds });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Update an attendee's status
 * @param {number} eventId - The ID of the event
 * @param {number} userId - The ID of the user to update
 * @param {string} status - The new status ('confirmed' or 'declined')
 * @returns {Promise<Object>} Updated attendee data
 */
export const updateAttendanceStatus = async (eventId, userId, status) => {
    try {
        const response = await api.put(`/events/${eventId}/attendees/${userId}`, { status });
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Remove an attendee from an event
 * @param {number} eventId - The ID of the event
 * @param {number} userId - The ID of the user to remove
 * @returns {Promise<Object>} Response data
 */
export const removeEventAttendee = async (eventId, userId) => {
    try {
        const response = await api.delete(`/events/${eventId}/attendees/${userId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

/**
 * Export event setlist as PowerPoint presentation
 * @param {number} eventId - The ID of the event to export
 * @returns {Promise<Blob>} PPTX file as blob
 */
export const exportEventSetlistPPTX = async (eventId) => {
    try {
        const response = await api.get(`/events/${eventId}/export/pptx`, {
            responseType: 'blob'
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};